<template>
  <div class="welcome-screen-analytics">
    <div
      v-if="isProviderTaxLotError"
      class="welcome-screen-analytics__error"
    >
      <div
        class="welcome-screen-analytics__caption welcome-screen-analytics__caption--normal"
      >
        Experimental tax-lot analytics could not be calculated.
      </div>
      <div class="welcome-screen-analytics__description">
        Disable provider tax lots in Analytics settings or retry after data
        repair.
      </div>
      <UIButton
        :leading="ArrowPathIcon"
        :loading="refreshing"
        :disabled="refreshing"
        @click="handleReload"
      >
        Refresh
      </UIButton>
    </div>
    <div
      v-else-if="isNoRepositoryError"
      class="welcome-screen-analytics__caption"
    >
      Unknown repository with name <strong>"{{ repositoryName }}"</strong>
    </div>
    <div
      v-else-if="isNoAnalyticsError"
      class="welcome-screen-analytics__caption"
    >
      No transactions or error calculating analytics <br />for
      <strong>{{ repositoryEndDate }}</strong>
      <DashboardsTimeline
        :disabled="isLoading"
        hide-slider
        hide-breadcrumb
        class="welcome-screen-analytics__timeline"
      />
    </div>
    <UILayoutNoData v-else-if="isReadonly" inline hide-question-icon>
      <template #text-description>
        No accounts connected with current repository
      </template>
      <template #text-question>What is a connectors?</template>
    </UILayoutNoData>
    <div
      v-else-if="isNoTransactionsError"
      class="welcome-screen-analytics__caption"
    >
      Get started by adding accounts and assets
    </div>
    <div v-else class="welcome-screen-analytics__error">
      <div class="welcome-screen-analytics__caption">Calculation error</div>
      <UIButton
        :leading="ArrowPathIcon"
        :loading="refreshing"
        :disabled="refreshing"
        @click="handleReload"
      >
        Refresh
      </UIButton>
    </div>
    <CreateActions v-if="isActionsVisible" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'

import { AnalyticsStatus, ReadonlyMode } from '@types'
import { READONLY_MODE } from '@/const'

import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

import { stringToLocalDateString } from '@/helpers/dates'

import CreateActions from '@/views/components/CreateActions.vue'
import DashboardsTimeline from '@/views/Dashboards/components/Timeline.vue'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UILayoutNoData } from '@ui/core'

const analyticsStore = useAnalyticsStore()
const repositoriesStore = useRepositoriesStore()

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const refreshing = ref(false)

const repositoryName = computed(() => repositoriesStore.getCurrentName)
const repositoryEndDate = computed(() =>
  stringToLocalDateString(repositoriesStore.getCurrentTimelineEnd),
)

const isLoading = computed(
  () => analyticsStore.isLoading || !analyticsStore.initFlag,
)

const errorStatus = computed(() => analyticsStore.getError?.status)

const isProviderTaxLotError = computed(
  () => errorStatus.value === AnalyticsStatus.PROVIDER_TAX_LOT_ERROR,
)

const isNoRepositoryError = computed(
  () => errorStatus.value === AnalyticsStatus.ERROR,
)

const isNoAnalyticsError = computed(
  () => errorStatus.value === AnalyticsStatus.NO_ANALYTICS_FOR_PERIOD,
)

const isNoTransactionsError = computed(
  () => errorStatus.value === AnalyticsStatus.NO_TRANSACTIONS,
)

const isActionsVisible = computed(
  () => !isReadonly?.value && isNoTransactionsError.value,
)

const handleReload = () => {
  refreshing.value = true
  window.location.reload()
}
</script>

<style>
.welcome-screen-analytics {
  @apply flex flex-col items-center;
  @apply gap-4;

  &__caption {
    @apply text-base sm:text-sm;
    @apply text-center;
    @apply uppercase;

    &--normal {
      @apply normal-case;
    }
  }

  &__description {
    @apply max-w-md text-center text-sm;
  }

  &__timeline {
    @apply !mt-6;

    .app-timeline__container,
    .app-timeline__panel-area {
      @apply hidden;
    }

    .app-timeline__panel {
      @apply rounded-lg -mx-2.5;
      @apply border-r border-l;
    }

    .app-timeline__panel-fields {
      @apply px-3;
    }
  }

  &__error {
    @apply flex flex-col items-center;
    @apply gap-4;
  }
}
</style>
